import { useSuspenseQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useMemo } from 'react';
import { DefaultValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useCourseCreate } from '@/module/Author/Course/model/CourseQuery';
import { CourseSchema, courseSchema, defaultCourseValues } from '@/module/Author/Course/model/CourseSchema';
import { getPublisherFindByTitleOptions } from '@/module/Publisher';

import { useAppNotification } from '@feature/utils';

import { CourseForm } from './CourseForm';

export function CourseCreate() {
  const { t } = useTranslation();
  const { mutate, isPending, isError, isSuccess } = useCourseCreate();

  const { data: defaultPublisher } = useSuspenseQuery(getPublisherFindByTitleOptions('University Press'));

  const defaultValues: DefaultValues<CourseSchema> = useMemo(
    () => ({
      ...defaultCourseValues,
      publisher: {
        id: defaultPublisher.id,
        name: defaultPublisher.title,
      },
    }),
    [defaultPublisher],
  );

  const notification = useAppNotification();
  const navigate = useNavigate();

  const onSubmit = (values: CourseSchema) => {
    mutate(values, {
      onError: notification.error,
      onSuccess: newCourse => {
        notification.success();
        navigate({ to: '/author/course/$courseId', params: { courseId: String(newCourse.id) } });
      },
    });
  };

  return (
    <CourseForm
      title={t('courses.form.title.create')}
      schema={courseSchema}
      defaultValues={defaultValues}
      values={undefined}
      isSubmitting={isPending}
      isError={isError}
      isSuccess={isSuccess}
      onSubmit={onSubmit}
      onCancel={() => navigate({ to: '..' })}
    />
  );
}
